'use client';

import { c as _c } from "react/compiler-runtime";
import type { Address, ChainId } from '@morpho-org/blue-sdk';
import { InterfaceChainUtils, parseApiAsset } from '@morpho-org/dapp-core';
import { Pagination } from '@morpho-org/ui-v6/client';
import { TableNoResults } from '@morpho-org/ui-v6/server';
import { useTable } from '@tanstack/react-table';
import { Table, TrBodyRow, TrHeaderGroup } from '@/components/common/Table';
import { TableScrollArea } from '@/components/common/Table/CompactTable/Table';
import { VAULT_TABLE_5_ROWS_HEIGHT } from '@/components/VaultPage/config';
import useTableSettings from '@/helpers/cookieStorage/userSettings/tableSettings/client/useTableSettings';
import { PageKey } from '@/helpers/cookieStorage/userSettings/types';
import { getParsedValue } from '@/helpers/formatters';
import { getMarketUrl } from '@/helpers/routing';
import { usePagination } from '@/hooks/usePagination';
import { trackExposureRowClicked } from '@/lib/analytics/client/trackers/exposure';
import type { MarketAllocationVaultData, PendingMarketActions } from './config/MarketAllocation.types';
import { MARKET_ALLOCATION_DEFAULT_TABLE_SETTINGS, MARKET_ALLOCATION_STORAGE_KEY, PAGE_SIZE } from './config/config';
import { columnLabelsMarketAllocation, getColumnsMarketAllocation, marketAllocationTableFeatures } from './config/MarketAllocation.columns';
import { useMarketAllocationData } from './hooks';
export interface MarketAllocationClientProps {
  chainId: ChainId;
  vault: MarketAllocationVaultData;
  /** `null` when the vault's pending actions could not be read: the table renders without them. */
  pendingActions: PendingMarketActions | null;
  /** `true` while the pending actions are still streaming in. */
  pendingActionsLoading?: boolean;
  /** Set when a market may be missing its spot APY or rewards. */
  allocationsPartial?: boolean;
}
const MarketAllocationClient = t0 => {
  const $ = _c(43);
  const {
    chainId,
    vault,
    pendingActions,
    pendingActionsLoading: t1,
    allocationsPartial: t2
  } = t0;
  const pendingActionsLoading = t1 === undefined ? false : t1;
  const allocationsPartial = t2 === undefined ? false : t2;
  const {
    address
  } = vault;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      defaultPageSize: PAGE_SIZE
    };
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  const {
    pagination,
    onPaginationChange
  } = usePagination(t3);
  const vaultAsset = getParsedValue(vault.asset, parseApiAsset);
  const {
    data: vaultAllocations,
    totalCount
  } = useMarketAllocationData(vault, pendingActions?.items ?? null);
  let t4;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      pageKey: PageKey.Vault,
      tableSettingsObjectKey: MARKET_ALLOCATION_STORAGE_KEY,
      defaultTableSettings: MARKET_ALLOCATION_DEFAULT_TABLE_SETTINGS
    };
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const {
    sorting,
    setSorting
  } = useTableSettings(t4);
  const t5 = marketAllocationTableFeatures;
  let t6;
  if ($[2] !== pagination || $[3] !== sorting) {
    t6 = {
      sorting,
      pagination
    };
    $[2] = pagination;
    $[3] = sorting;
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  const t7 = getColumnsMarketAllocation({
    vaultAddress: address as Address,
    chainId,
    vaultAsset
  });
  let t8;
  if ($[5] !== onPaginationChange || $[6] !== setSorting || $[7] !== t5 || $[8] !== t6 || $[9] !== t7 || $[10] !== vaultAllocations) {
    t8 = {
      features: t5,
      data: vaultAllocations,
      onSortingChange: setSorting,
      state: t6,
      columns: t7,
      enableSortingRemoval: false,
      sortDescFirst: true,
      onPaginationChange
    };
    $[5] = onPaginationChange;
    $[6] = setSorting;
    $[7] = t5;
    $[8] = t6;
    $[9] = t7;
    $[10] = vaultAllocations;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  const vaultAllocationTable = useTable(t8);
  const t9 = pendingActionsLoading ? "loading" : "loaded";
  let t10;
  if ($[12] !== address || $[13] !== chainId || $[14] !== vaultAllocationTable) {
    t10 = vaultAllocationTable.getRowModel().rows.length > 0 && <Table.Table tableVariant="compact" className="[&_.td-content]:pr-[16px] [&_th]:pr-[16px]"><Table.Thead>{vaultAllocationTable.getHeaderGroups().map(_temp)}</Table.Thead><Table.Tbody>{vaultAllocationTable.getRowModel().rows.map(row => {
          const {
            market
          } = row.original;
          const marketUrl = market.collateralAsset ? getMarketUrl({
            identifier: InterfaceChainUtils.getIdentifierById(chainId),
            marketId: market.marketId,
            collateralSymbol: market.collateralAsset.symbol ?? "",
            loanSymbol: market.loanAsset.symbol ?? ""
          }) : undefined;
          return <TrBodyRow key={row.id} row={row} data-testid={`market-allocation-row-${market.marketId.toLowerCase()}`} href={marketUrl} onNavigate={marketUrl ? () => trackExposureRowClicked({
            sourceVaultAddress: address,
            sourceVaultVersion: "v1",
            sourceChainId: chainId,
            destinationType: "market",
            destinationIdentifier: market.marketId,
            destinationChainId: market.loanAsset.chain.id
          }) : undefined} />;
        })}</Table.Tbody></Table.Table>;
    $[12] = address;
    $[13] = chainId;
    $[14] = vaultAllocationTable;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== vaultAllocationTable) {
    t11 = vaultAllocationTable.getRowModel().rows.length === 0 && <TableNoResults text="No market allocation found." height={VAULT_TABLE_5_ROWS_HEIGHT} style={{
      borderRadius: "0"
    }} />;
    $[16] = vaultAllocationTable;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] !== t10 || $[19] !== t11) {
    t12 = <TableScrollArea className="grid h-full w-full">{t10}{t11}</TableScrollArea>;
    $[18] = t10;
    $[19] = t11;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] !== allocationsPartial) {
    t13 = allocationsPartial && <p className="text-secondary-foreground px-sm text-xs" data-testid="market-allocation-allocations-partial">Some market APYs or rewards could not be loaded, so they may be missing.</p>;
    $[21] = allocationsPartial;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] !== pendingActions?.partial) {
    t14 = pendingActions?.partial && <p className="text-secondary-foreground px-sm text-xs" data-testid="market-allocation-pending-partial">Some pending governance actions could not be loaded, so pending caps may be missing.</p>;
    $[23] = pendingActions?.partial;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  const t15 = vaultAllocationTable.previousPage;
  const t16 = vaultAllocationTable.nextPage;
  const t17 = vaultAllocationTable.setPageIndex;
  let t18;
  if ($[25] !== vaultAllocationTable) {
    t18 = vaultAllocationTable.getCanPreviousPage();
    $[25] = vaultAllocationTable;
    $[26] = t18;
  } else {
    t18 = $[26];
  }
  let t19;
  if ($[27] !== vaultAllocationTable) {
    t19 = vaultAllocationTable.getCanNextPage();
    $[27] = vaultAllocationTable;
    $[28] = t19;
  } else {
    t19 = $[28];
  }
  let t20;
  if ($[29] !== pagination || $[30] !== t18 || $[31] !== t19 || $[32] !== totalCount || $[33] !== vaultAllocationTable.nextPage || $[34] !== vaultAllocationTable.previousPage || $[35] !== vaultAllocationTable.setPageIndex) {
    t20 = <Pagination loading={false} pagination={pagination} totalItemsCount={totalCount} previousPage={t15} nextPage={t16} setPage={t17} canPreviousPage={t18} canNextPage={t19} />;
    $[29] = pagination;
    $[30] = t18;
    $[31] = t19;
    $[32] = totalCount;
    $[33] = vaultAllocationTable.nextPage;
    $[34] = vaultAllocationTable.previousPage;
    $[35] = vaultAllocationTable.setPageIndex;
    $[36] = t20;
  } else {
    t20 = $[36];
  }
  let t21;
  if ($[37] !== t12 || $[38] !== t13 || $[39] !== t14 || $[40] !== t20 || $[41] !== t9) {
    t21 = <div className="contents" data-pending-actions={t9}>{t12}{t13}{t14}{t20}</div>;
    $[37] = t12;
    $[38] = t13;
    $[39] = t14;
    $[40] = t20;
    $[41] = t9;
    $[42] = t21;
  } else {
    t21 = $[42];
  }
  return t21;
};
export default MarketAllocationClient;
function _temp(headerGroup) {
  return <TrHeaderGroup key={headerGroup.id} columnLabels={columnLabelsMarketAllocation} headerGroup={headerGroup} />;
}